import React, { memo, useState, useEffect } from "react";
import styles from "./Sidebar.module.scss";
import { sideBarNavigation } from "@/lib/sidebarNavigation";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { combineClasses, getCurrentUser, addBaseUrl } from "@/lib/utils";
import { setSidebarOpen, setModalOpen } from "@/store/slices/globalSlice";
import { MODAL_ID } from "@/lib/constants";
import type { RootState } from "@/store/rootReducer";
import type { AppDispatch } from "@/store/store";
import type { SidebarItem } from "@/lib/sidebarNavigation";

interface SidebarProps {
  toggleSidebarOpen: (open: boolean) => (e: React.MouseEvent) => void;
}

/* ─────────────────────────────────────────────────────────────────
   All icons: viewBox 0 0 24 24, stroke-width 1.7, currentColor
───────────────────────────────────────────────────────────────── */
const ICON_PATHS: Record<string, string> = {
  dashboard:
    '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/>',
  assessment:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 9h8M8 13h5"/><path d="m15.5 16 1.5 1.5 2.5-2.5"/>',
  library:
    '<path d="M4 5v14M8 5v14M13 5l4 14M19 6l1 .3"/><path d="M3 5h6M11 5h4"/>',
  settings:
    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  code: '<text x="0" y="17" font-size="14" font-family="monospace">&lt;/&gt;</text>',
  file: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="14" y2="17"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  question:
    '<circle cx="12" cy="12" r="9"/><path d="M9.2 9a3 3 0 0 1 5.6 1c0 2-3 2.5-3 4"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
  freeform:
    '<path d="M4 5h16M4 10h16M4 15h10"/><path d="m15 19 2 2 4-4"/>',
  user:
    '<circle cx="12" cy="7" r="4"/><path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"/>',
  users:
    '<circle cx="9" cy="8" r="3.2"/><path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5"/><path d="M16 4.5a3.2 3.2 0 0 1 0 6.3M21 20c0-2.6-1.6-4.2-4-4.8"/>',
  logout:
    '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>',
  chevronDown: '<polyline points="6 9 12 15 18 9"/>',
};

/** Renders an SVG icon by name from the prototype icon set */
const Icon: React.FC<{ name: string; size?: number }> = ({ name, size = 18 }) => {
  const d = ICON_PATHS[name] || ICON_PATHS.code;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width={size}
      height={size}
      dangerouslySetInnerHTML={{ __html: d }}
      aria-hidden="true"
    />
  );
};

/** Maps route pageName / path to a prototype icon key */
const getIconName = (pageName: string | undefined, path: string): string => {
  const p = (pageName || "").toLowerCase();
  const r = (path || "").toLowerCase();
  if (p.includes("dashboard")) return "dashboard";
  if (p.includes("assessment")) return "assessment";
  if (p.includes("custom library") || p.includes("library")) return "library";
  if (p.includes("setting") || p.includes("roles") || p.includes("permission")) return "settings";
  if (p.includes("email")) return "mail";
  if (p.includes("file")) return "file";
  if (p.includes("challenge") || r.includes("challenge")) return "code";
  if (p.includes("question") || r.includes("question")) return "question";
  if (p.includes("free-form") || p.includes("freeform") || r.includes("free-form")) return "freeform";
  if (p.includes("personal info") || p.includes("profile")) return "user";
  if (p.includes("user")) return "users";
  return "code";
};

const Sidebar: React.FC<SidebarProps> = () => {
  const { sidebarOpen } = useSelector((state: RootState) => state.global);
  const { profile } = useSelector((state: RootState) => state.user);
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

  const currentUserRole = profile?.role?.toUpperCase();
  const canManage = currentUserRole === "OWNER" || currentUserRole === "MANAGER" || currentUserRole === "ADMIN";
  const isAdminOrOwner = currentUserRole === "OWNER" || currentUserRole === "ADMIN";

  // Load profile for user card


  // Auto-expand groups whose child is active
  useEffect(() => {
    sideBarNavigation.forEach((item) => {
      if (item.children && item.children.length > 0) {
        const isChildActive = location.pathname.includes(item.path);
        if (isChildActive) {
          setOpenDropdowns((prev) => ({ ...prev, [item.pageName || ""]: true }));
        }
      }
    });
  }, [location.pathname]);

  const toggleDropdown = (pageName: string) => {
    setOpenDropdowns((prev) => ({ ...prev, [pageName]: !prev[pageName] }));
  };

  const handleLinkClick = () => {
    if (window.innerWidth <= 768) {
      dispatch(setSidebarOpen(false));
    }
  };

  const handleLogout = () => {
    dispatch(setModalOpen({ id: MODAL_ID.LOGOUT }));
  };

  const cachedUser = getCurrentUser();
  const displayName =
    profile?.name ||
    (profile as any)?.firstName ||
    (profile as any)?.fullName ||
    cachedUser?.userName ||
    "User";
  const displayRole = (profile as any)?.role || cachedUser?.role || "Member";
  const avatarUrl = profile?.profileUrl ? addBaseUrl(profile.profileUrl) : null;

  return (
    <div className={combineClasses(styles.leftSideContainer, !sidebarOpen && styles.sidebarClosed)}>

      {/* ── Brand / Logo ── */}
      <div className={styles.logoContainer}>
        <div className={styles.mainIconDiv}>
          <span>{"</>"}</span>
        </div>
        <span className={styles.logoText}>
          <span >N</span>
          <span className={styles.logoCode}>Code</span>
        </span>
      </div>

      {/* ── Navigation ── */}
      <nav className={styles.navContainer}>
        {sideBarNavigation
          .filter((item) => {
            // Filter out navigation groups that have children but none of their children are visible
            if (item.children && item.children.length > 0) {
              const visibleChildren = item.children.filter((child) => {
                if (child.pageName === "User Management") {
                  return canManage;
                }
                if (child.pageName === "Organization Settings") {
                  return true;
                }
                if (child.pageName === "Emails" && currentUserRole === "EDITOR") {
                  return false;
                }
                return true;
              });
              return visibleChildren.length > 0;
            }
            return true;
          })
          .map((data: SidebarItem) => {
            const iconName = getIconName(data.pageName, data.path);
            const filteredChildren = data.children?.filter((child) => {
              if (child.pageName === "User Management") {
                return canManage;
              }
              if (child.pageName === "Organization Settings") {
                return true;
              }
              if (child.pageName === "Emails" && currentUserRole === "EDITOR") {
                return false;
              }
              return true;
            });
            const hasChildren = filteredChildren && filteredChildren.length > 0;
            const isOpen = !!openDropdowns[data.pageName || ""];

            if (hasChildren) {
              const isGroupActive = location.pathname.startsWith(data.path);
              return (
                <div key={data.id}>
                  <div
                    className={combineClasses(
                      styles.navLink,
                      isGroupActive && styles.activeLink
                    )}
                    onClick={() => {
                      toggleDropdown(data.pageName || "");
                      if (!sidebarOpen) dispatch(setSidebarOpen(true));
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && toggleDropdown(data.pageName || "")}
                  >
                    <span className={styles.icon}>
                      <Icon name={iconName} size={18} />
                    </span>
                    <span className={styles.linkPageName}>{data.pageName}</span>
                    <span className={combineClasses(styles.arrow, isOpen && styles.rotate)}>
                      <Icon name="chevronDown" size={14} />
                    </span>
                  </div>

                  {isOpen && (
                    <div className={styles.dropdown}>
                      {filteredChildren.map((child) => {
                        const childPath = child.path.startsWith("/")
                          ? child.path
                          : `${data.path}/${child.path}`;
                        const childIcon = getIconName(child.pageName, child.path);
                        return (
                          <NavLink 
                            key={child.id} 
                            to={childPath} 
                            className={({ isActive }) =>
                              isActive ? styles.childActive : undefined
                            }
                            onClick={handleLinkClick}
                          >
                            <span className={styles.childIcon}>
                              <Icon name={childIcon} size={16} />
                            </span>
                            {child.pageName}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={data.id}
                to={data.path}
                className={({ isActive }) =>
                  combineClasses(styles.navLink, isActive && styles.activeLink)
                }
                onClick={handleLinkClick}
              >
                <span className={styles.icon}>
                  <Icon name={iconName} size={18} />
                </span>
                <span className={styles.linkPageName}>{data.pageName}</span>
              </NavLink>
            );
          })}
      </nav>

      {/* ── User card ── */}
      <div 
        className={styles.userCard} 
        onClick={() => {
          navigate('/roles-permissions/personal-info');
          handleLinkClick();
        }} 
        role="button" 
        tabIndex={0}
      >
        <div className={styles.userAvatar}>
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} className={styles.userAvatarImg} />
          ) : (
            <span>{displayName.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className={styles.userInfo}>
          <span className={styles.userName}>{displayName}</span>
          <span className={styles.userRole}>{displayRole}</span>
        </div>
      </div>

      {/* ── Logout ── */}
      <div className={styles.logoutButton} onClick={handleLogout} role="button" tabIndex={0}>
        <div className={styles.navLink}>
          <span className={styles.icon}>
            <Icon name="logout" size={18} />
          </span>
          <span className={styles.linkPageName}>Logout</span>
        </div>
      </div>

    </div>
  );
};

export default memo(Sidebar);
